import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import FilmsPromo from '../../../assets/images/FilmsPromo.png';
import {
  ButtonClassNames,
  GlobalRouteNames,
  LocalStorageItems,
  ModalVariants,
  SuccessRouteNames,
} from '../../../models/Common';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { paymentsApi } from '../../../services/apiServices/user/payments';
import { useAppDispatch } from '../../../hooks/redux';
import { modalSlice } from '../../../store/reducers/ModalSlice';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { usePlaton } from '../../../hooks/usePlaton';
import { openInSameTab } from '../../../services/domService';
import { findViewValuesHelper, getErrorMessage } from '../../../utils';
import { View } from '../../../models/Calculate';
import { CurrentPrice } from '../../../hooks/useCalculate';
import { CustomShareParamsActivation } from '../../../models/ServicePresets';

interface ISharePromoBRIZTVPremium {
  customerShareParams: CustomShareParamsActivation;
}

export type SharePromoKeysFromCurrentPrice = 'amount' | 'payAmount' | 'view' | 'discountAmount' | 'fromDate' | 'toDate';
export type SharePromo3Month = Pick<CurrentPrice, SharePromoKeysFromCurrentPrice>;

export const defaultSharePromo3Month = {
  discountAmount: 0,
  payAmount: 0,
  fromDate: '',
  toDate: '',
  amount: 0,
  view: {} as View,
};

export const SharePromoBRIZTVPremium: FC<ISharePromoBRIZTVPremium> = ({ customerShareParams }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const history = useHistory();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { platonData, setPlatonData, getLinkToPlatonFormAPI, reFetchPlatonAPI } = usePlaton();
  const [calculateCostAPI, { isLoading: isLoadingCurrentCalculation }] = paymentsApi.useCalculateCostMutation();
  const [serviceActivateAPI] = paymentsApi.useServicePaymentMutation();
  const [createServiceOrderAPI] = paymentsApi.useCreateServiceOrderMutation();
  const [isClicked, setIsClicked] = useState(false);

  const paramsForCalculation = {
    accountId: customerShareParams.accountId,
    accountType: customerShareParams.accountType,
    shares: customerShareParams.shares,
  };

  const [currentCalculation, setCurrentCalculation] = useState<SharePromo3Month>(defaultSharePromo3Month);

  const handlePay = async () => {
    try {
      await createServiceOrderAPI(paramsForCalculation)
        .unwrap()
        .then((resp) => {
          setPlatonData({
            ...platonData,
            amount: resp.Amount,
            order: resp.Code,
            description: customerShareParams.shareName,
          });
          localStorage.setItem(LocalStorageItems.ServiceName, customerShareParams.shareName);
          localStorage.setItem(LocalStorageItems.FromDate, currentCalculation.fromDate);
          localStorage.setItem(LocalStorageItems.ToDate, currentCalculation.toDate);
          reFetchPlatonAPI({
            ...platonData,
            amount: resp.Amount,
            order: resp.Code,
            description: customerShareParams.shareName,
          });
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handleActivate = async () => {
    try {
      await serviceActivateAPI(paramsForCalculation)
        .unwrap()
        .then(() => {
          localStorage.setItem(LocalStorageItems.ServiceName, customerShareParams.shareName);
          localStorage.setItem(LocalStorageItems.FromDate, currentCalculation.fromDate);
          localStorage.setItem(LocalStorageItems.ToDate, currentCalculation.toDate);
          history.push(`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.ServiceActivationSuccessfulPage}`);
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handleActivateModalShare3Month = (data: SharePromo3Month) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.PromoShareThreeMonths,
        content: {
          isShare: true,
          shareCalculation: data,
          handleActivate,
          handlePay,
          customShareName: customerShareParams.shareName,
          buttonShareName: customerShareParams.actionName,
          images: customerShareParams.image,
        },
      }),
    );
  };

  const handlePreAction = async () => {
    if (paramsForCalculation.accountId) {
      try {
        await calculateCostAPI(paramsForCalculation)
          .unwrap()
          .then((resp) => {
            setCurrentCalculation({
              discountAmount: resp.discount_amount,
              payAmount: resp.pay_amount,
              amount: resp.amount,
              view: resp.view,
              fromDate: `${new Date(+findViewValuesHelper(resp.view, 'fts') * 1000)}`,
              toDate: `${new Date(+findViewValuesHelper(resp.view, 'tts') * 1000)}`,
            });
          });
      } catch (err) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }

      setIsClicked(true);
    }
  };

  useEffect(() => {
    if (!isLoadingCurrentCalculation && isClicked) {
      handleActivateModalShare3Month(currentCalculation);
    }
    return () => {
      setIsClicked(false);
    };
  }, [isClicked]);

  useEffect(() => {
    if (getLinkToPlatonFormAPI) {
      openInSameTab(getLinkToPlatonFormAPI.short_link);
      localStorage.setItem(LocalStorageItems.TopUpAmount, `${platonData.amount}`);
    }
  }, [getLinkToPlatonFormAPI]);

  return (
    <div className="payment__promo">
      <img src={FilmsPromo} className="payment__promo__img" alt="pic" />
      <div className="payment__promo__content">
        <div className="payment__promo__content__text">
          <h5>{customerShareParams.shareDescription || t('servicePayment.sharePromoThreeMonthsDescription')}</h5>
          <h5>{t('servicePayment.sharePromoThreeMonthsTerm', { until: customerShareParams.activationShareEnd })}</h5>
        </div>
        <div className="payment__promo__content__btn-group">
          <CustomMUIButton
            className={ButtonClassNames.PromoFulfilled}
            label={customerShareParams.preActionName || t('buttons.getDiscount')}
            handleClick={handlePreAction}
            isBold
          />
        </div>
      </div>
    </div>
  );
};
